<template>
  <div class="proposals-read-status">
    <form-select
      :key="`${attempt.id}-status`"
      :options="options"
      :value="value"
      :style="style"
      class="proposals-read-status-select"
      label="Status da tentativa"
      :disabled="outdated || draft"
      @input="update"
    />

    <template v-if="updating">
      <modal-base
        v-if="filling"
        title="Envio de proposta"
        @close="close"
      >
        <proposals-read-used
          :proposal="proposal"
          :customer="customer"
          :attempt="attempt"
          :visible-fields="fields"
          class="proposals-read-status-used"
          @success="filled"
        />
      </modal-base>

      <modal-confirmation
        v-else
        :loading="loading"
        title="Atualizar status"
        @close="close"
        @confirm="submit"
      >
        <template v-if="integrating">
          Enviar proposta para o <b>{{ attempt.bank ? attempt.bank.tradename || attempt.bank.name : 'Banco desconhecido' }}</b>?
        </template>

        <template v-else>
          Alterar o status da proposta para

          <b :style="preview">{{ prettified }}</b>?
        </template>
      </modal-confirmation>
    </template>
  </div>
</template>

<script>
  import { mapState } from 'vuex';

  import FormSelect from '@cordel/components/Form/Select.vue';

  import ModalBase from '@cordel/components/Modal/Base.vue';
  import ModalConfirmation from '@cordel/components/Modal/Confirmation.vue';

  import status, { color, contrast } from '@cordel/helpers/colors/proposals';

  import BANKS from '@sdk/helpers/domains/banks';
  import STATUS from '@sdk/helpers/domains/status';
  import { integrated } from '@sdk/helpers/misc/installments';

  import CONSTANTS from './Information/constants';

  import ProposalsReadUsed from './Used.vue';

  import {
    KM,
    PLATE,
    RENAVAM,
    CHASSIS,
    COLOR,
    LICENSING_UF,
    LICENSING_CITY,
  } from './Used/fields';

  export default {
    name: 'ProposalsReadStatus',
    components: {
      FormSelect,

      ModalBase,
      ModalConfirmation,

      ProposalsReadUsed,
    },
    props: {
      proposal: {
        type: Object,
        default() {
          return {};
        },
      },
      attempt: {
        type: Object,
        default() {
          return {};
        },
      },
      customer: {
        type: Object,
        default() {
          return {};
        },
      },
    },
    data() {
      return {
        updating: null,
        unfilled: true,
      };
    },
    computed: {
      ...mapState([
        'proposals',
        'proposalAttempts',
      ]),

      loading() {
        return this.proposalAttempts.read.loading;
      },

      integrated() {
        return integrated(this.attempt, this.proposal.customer);
      },
      integrating() {
        return this.integrated && this.updating === STATUS.analyze.value;
      },

      fields() {
        const code = this.attempt.bank && this.attempt.bank.febraban_code;

        if ([BANKS.HONDA.code].includes(code)) {
          return [
            PLATE,
            CHASSIS,
            COLOR,
            RENAVAM,
          ];
        }

        if ([BANKS.BV.code].includes(code)) {
          return [
            KM,
          ];
        }

        if ([BANKS.FONTECRED.code].includes(code)) {
          return [
            PLATE,
            RENAVAM,
          ];
        }

        if ([BANKS.SAFRA.code].includes(code)) {
          return [
            LICENSING_UF,
            LICENSING_CITY,
          ];
        }

        if ([BANKS.BRADESCO.code].includes(code)) {
          return [
            LICENSING_UF,
          ];
        }

        if ([BANKS.KUNA.code].includes(code)) {
          return [
            PLATE,
          ];
        }

        return [];
      },
      filling() {
        const zero = this.proposal.zero_km;
        const code = this.attempt.bank && this.attempt.bank.febraban_code;

        let included = false;

        if (zero) {
          if ([BANKS.BRADESCO.code].includes(code)) {
            included = this.proposal.licensing_uf !== this.customer.address.state.abbreviation;
          }
        } else {
          included = [
            BANKS.HONDA.code,
            BANKS.BV.code,
            BANKS.FONTECRED.code,
            BANKS.SAFRA.code,
            BANKS.KUNA.code,
          ].includes(code);
        }

        return included
               && this.integrating
               && this.unfilled
               && this.fields.length;
      },

      status() {
        return this.attempt.state;
      },
      value() {
        return this.attempt.replaced_by_proposal_attempt_id ? 'Desatualizada' : Object
          .values(STATUS)
          .find(i => i.identifier === this.status)
          .value;
      },

      style() {
        if (this.outdated || this.draft) {
          return {};
        }

        return {
          '--background-color': `var(--cordel-color-${color(this.status)})`,
          '--color': `var(--cordel-color-${contrast(this.status)})`,
        };
      },
      outdated() {
        return this.attempt.replaced_by_proposal_attempt_id;
      },
      draft() {
        return this.attempt.bank && this.attempt.bank.name === CONSTANTS.BANKS.DRAFT;
      },

      preview() {
        return {
          color: `var(--cordel-color-${color(this.updating)})`,
        };
      },
      prettified() {
        return status(this.updating);
      },

      options() {
        if (!this.integrated) {
          return Object.values(STATUS);
        }

        return Object.keys(STATUS).map((i) => {
          const option = { ...STATUS[i] };
          const disabled = { ...STATUS[i], disabled: true };

          if (option.identifier === this.status) {
            return option;
          }

          if (option.identifier === STATUS.check.identifier) {
            const {
              external_proposal_uuid: proposalUUID,
              external_simulation_uuid: simulationUUID,
            } = this.attempt;

            if (simulationUUID && !proposalUUID) {
              return option;
            }

            return disabled;
          }

          if (option.identifier === STATUS.analyze.identifier) {
            if ([
              STATUS.check.identifier,
              STATUS.preapprove.identifier,
              STATUS.hang.identifier,
            ].includes(this.status)) {
              option.label = 'Enviar para o banco';

              return option;
            }

            return disabled;
          }

          if ([
            STATUS.hang.identifier,
            STATUS.restrict.identifier,
            STATUS.refuse.identifier,
          ].includes(option.identifier)) {
            if (this.status === STATUS.check.identifier) {
              return option;
            }

            return disabled;
          }

          if (option.identifier === STATUS.invoice.identifier) {
            if (this.status === STATUS.approve.identifier) {
              return option;
            }

            return disabled;
          }

          if (option.identifier === STATUS.dismiss.identifier) {
            return option;
          }

          return disabled;
        });
      },
    },
    methods: {
      update(updating) {
        this.updating = updating;
      },
      close() {
        this.unfilled = true;
        this.updating = null;
      },
      filled() {
        this.unfilled = false;
      },

      submit() {
        this.$store
          .dispatch('proposalAttempts/read', {
            proposal_id: this.proposal.id,
            id: this.attempt.id,
            status: this.updating,
          })
          .finally(this.submitted);
      },
      submitted() {
        this.updating = null;
      },
    },
  };
</script>

<style lang="scss">
  .proposals-read-status {
    .cordel-select-chevron {
      color: inherit;
    }
  }

  .proposals-read-status-used {
    .cordel-sections {
      margin: 0;
    }

    .cordel-sections-buttons {
      margin-left: 0;
      margin-right: 0;
      padding-bottom: 0;
    }
  }
</style>

<style lang="scss" scoped>
  @import '@cordel/assets/stylesheets/mixins/reset.scss';
  @import '@cordel/assets/stylesheets/variables/breakpoints.scss';

  .proposals-read-status {
  }

  .proposals-read-status-select {
    &:not(.state--disabled):not(.state--loading) {
      background-color: var(--background-color);
      color: var(--color);
    }

    &:not(:focus):not(.state--disabled):not(.state--loading):not(.state--error) {
      box-shadow: 0 0 0 var(--cordel-border-width-soft) var(--background-color);
    }
  }
</style>
